import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';

import { Flex, Spacer } from '@zillow/constellation';
import { Button, FormField, FormHelp, Input, Label } from '@zillow/constellation-10';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import PostingActions from '../../actions/PostingActions';

import numberUtils from '../../utils/numberUtils';
import propertyUtils from '../../utils/propertyUtils';

import AnalyticsConstants from '../../constants/AnalyticsConstants';

import { DEFAULT_VERIFICATION_CONFIG } from '../Modal/VerificationModal/constants';

import { VerificationModal } from '../Modal/VerificationModal';
import PhoneNumberDisplayInfo from '../PhoneNumberDisplayInfo';

import type React from 'react';
import type { IError } from '../../interfaces';

const POSTING_ACTIONS_PROPERTY_EDIT_OPTIONS = {
  componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.CONTACT_INFO,
  preserveFieldName: false,
  reserveErrorObject: true,
} as const;

type PhoneData = {
  contactPhone: string;
  contactPhoneDateVerified: string;
  listingAlias: string;
  trackingPhoneNumber: string;
};

type EditResponse = {
  dateVerified?: string;
  contactPhone?: string;
};

type PhoneNumberSaveArgs = {
  contactPhone: string | null;
};

type Props = {
  contactPhoneRef?: React.LegacyRef<HTMLDivElement>;
  isInputFormDisabled?: boolean;
  onPhoneNumberSave: (args: PhoneNumberSaveArgs) => void;
  phoneData: PhoneData;
  showPhoneNumberDisplayInfo?: boolean;
  updateErrorObj?: (args: PhoneNumberSaveArgs) => void;
  updateFetchingStatus?: (value: boolean) => void;
};

const AddEditPhoneNumber = ({
  phoneData: { contactPhone, contactPhoneDateVerified, listingAlias },
  contactPhoneRef,
  isInputFormDisabled = false,
  showPhoneNumberDisplayInfo = true,
  onPhoneNumberSave,
  updateErrorObj = () => {},
  updateFetchingStatus = () => {},
}: Props) => {
  const dispatch = useDispatch();

  const currentProperty = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string>(contactPhone || '');
  const [datePhoneVerified, setDatePhoneVerified] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  useEffect(() => {
    if (contactPhone) {
      setVerifiedPhoneNumber(contactPhone);
      setDatePhoneVerified(contactPhoneDateVerified || '');
    }
  }, [contactPhone, contactPhoneDateVerified]);

  const handlePhoneNumberAdded = (cleanPhoneNumber: string) => {
    updateFetchingStatus(true);

    return (
      dispatch(
        PostingActions.property.edit(
          {
            fieldName: 'contactPhone',
            value: cleanPhoneNumber,
            propertyId: listingAlias,
          },
          POSTING_ACTIONS_PROPERTY_EDIT_OPTIONS,
        ),
      )
        // @ts-expect-error
        .then((response: EditResponse) => {
          const addedPhoneNumber = response?.contactPhone || cleanPhoneNumber;
          setVerifiedPhoneNumber(addedPhoneNumber);
          setDatePhoneVerified(response?.dateVerified || new Date().toString());
          onPhoneNumberSave({ contactPhone: addedPhoneNumber });
          updateErrorObj({ contactPhone: null });

          return Promise.resolve();
        })
        .catch((err: IError) => Promise.reject(err))
        .finally(() => {
          updateFetchingStatus(false);
        })
    );
  };

  const handleVerificationConfirmation = (phoneNumber: string) => {
    updateFetchingStatus(true);

    return (
      dispatch(
        PostingActions.property.edit(
          {
            fieldName: 'contactPhone',
            value: phoneNumber,
            propertyId: listingAlias,
          },
          POSTING_ACTIONS_PROPERTY_EDIT_OPTIONS,
        ),
      )
        // @ts-expect-error
        .then((response: EditResponse) => {
          const verifiedContactPhone = response?.contactPhone || phoneNumber;
          setVerifiedPhoneNumber(verifiedContactPhone);
          setDatePhoneVerified(response?.dateVerified || new Date().toString());
          onPhoneNumberSave({ contactPhone: verifiedContactPhone });
          updateErrorObj({ contactPhone: null });

          return Promise.resolve();
        })
        .catch((error: IError) => {
          return Promise.reject(error);
        })
        .finally(() => {
          updateFetchingStatus(false);
        })
    );
  };

  return (
    <>
      {(verifiedPhoneNumber || !isInputFormDisabled) && (
        <>
          {contactPhoneRef && <div ref={contactPhoneRef} />}
          <FormField>
            <Label required={!isInputFormDisabled} disabled={isInputFormDisabled}>
              Phone number
            </Label>

            {verifiedPhoneNumber ? (
              <>
                <Flex display="flex">
                  <Spacer marginRight="sm">
                    <Input
                      disabled={true}
                      size={isScreenDesktop ? 'lg' : 'md'}
                      aria-required="true"
                      name="contactPhone"
                      type="tel"
                      value={numberUtils.formatPhoneNumberInput(verifiedPhoneNumber || '')}
                      aria-describedby={datePhoneVerified ? 'verification' : undefined}
                      className="fs-mask"
                      css={{
                        width: 'auto',
                        maxWidth: '300px',
                        '&[disabled]': {
                          opacity: 1,
                        },
                      }}
                    />
                  </Spacer>

                  {!isInputFormDisabled && (
                    <Button
                      size="sm"
                      tone="neutral"
                      emphasis="outlined"
                      onClick={() => setShowPhoneVerificationModal(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Flex>

                <FormHelp id="verification">
                  {isValid(new Date(datePhoneVerified))
                    ? `Verified on ${format(new Date(datePhoneVerified), 'MMMM d, yyyy')}`
                    : 'Previously verified'}
                </FormHelp>
              </>
            ) : (
              <Button size="sm" tone="neutral" emphasis="outlined" onClick={() => setShowPhoneVerificationModal(true)}>
                Add phone number
              </Button>
            )}
          </FormField>
        </>
      )}

      {showPhoneVerificationModal && (
        <VerificationModal
          headerText="Add and verify phone number"
          onContentValueAdded={handlePhoneNumberAdded}
          onVerificationConfirmation={handleVerificationConfirmation}
          showVerificationModal={setShowPhoneVerificationModal}
          stepsConfig={DEFAULT_VERIFICATION_CONFIG}
        />
      )}

      {!isInputFormDisabled && verifiedPhoneNumber && showPhoneNumberDisplayInfo && !isRoomForRent && (
        <Spacer marginTop="sm">
          <PhoneNumberDisplayInfo />
        </Spacer>
      )}
    </>
  );
};

export default AddEditPhoneNumber;
